import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";

import {
  Flex,
  Typography,
  Form,
  Spin,
  App,
  Tag,
  Button,
  Card,
  Divider,
  Space,
} from "antd";

import routes from "@/routes";

import Breadcrumbs from "@/components/ui/Breadcrumbs";

import { getFormItem } from "@/api/requests";
import { useRequestGroups } from "./hooks/useRequestGroups";
import BackBtn from "@/components/ui/BackBtn";
import UnitsCard from "./components/UnitsCard";
import MainInfo from "./components/MainInfo";
import GroupsCard from "./components/GroupsCard";
import AccessUnitsCard from "./components/AccessUnitsCard";
import TaskTitleTemplateCard from "./components/TaskTitleTemplateCard";
import AccessCard from "./components/AccessCard";

const RequestSettings = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { message } = App.useApp();
  const [data, setData] = useState(null);
  const stickyHeaderRef = useRef(null);

  const [form] = Form.useForm();

  const [isDeleted, setIsDeleted] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isPublic, setIsPublic] = useState(true);

  const breadcrumbItems = useMemo(
    () => [
      { title: "Административная панель" },
      {
        title: "Настройки системы",
        href: routes.administration.systemSettings,
      },
      {
        title: isDeleted
          ? "Тип заявки"
          : isNew
            ? "Создание типа заявки"
            : "Редактирование типа заявки",
      },
    ],
    [isNew, isDeleted],
  );

  const {
    groups,
    setGroups,
    pending,
    setPending,
    isDragDisabled,
    addGroup,
    toggleEdit,
  } = useRequestGroups();

  const fetchData = useCallback(async () => {
    if (isNew) return;
    try {
      setPending(true);
      const data = await getFormItem({ uuid: id }, true);
      setIsDeleted(!!data.deleted);
      setIsHidden(!data.is_visible);
      setIsPublic(data.access_mode === "public");
      setGroups(
        (data.groups || [])
          .sort((a, b) => a.idx - b.idx)
          .map((g) => ({
            ...g,
            id: g.uuid,
            isEditing: false,
            isNew: false,
            fields: (g.fields || [])
              .sort((a, b) => a.idx - b.idx)
              .map((f) => ({
                ...f,
                isNew: false,
                options: (f.options || [])
                  .sort((a, b) => a.idx - b.idx)
                  .map((o) => ({
                    ...o,
                    isNew: false,
                  })),
              })),
          })),
      );
      setData(data);

      form.setFieldsValue({
        name: data.name,
        description: data.description,
        is_visible: data.is_visible,
        enable_attachments: data.enable_attachments,
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || String(error),
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  }, [id, message, form, isNew, setGroups, setPending]);

  useEffect(() => {
    // Загрузка данных типа заявки по id, если id присутствует
    if (!isNew) {
      fetchData();
    } else {
      form.setFieldsValue({ name: "", description: "" });
    }
  }, [isNew, fetchData, form]);

  const scrollTo = (elementId) => {
    const targetElement = document.getElementById(elementId);
    if (!targetElement) return;

    const getScrollContainer = (element) => {
      let parent = element.parentElement;

      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const isScrollable =
          (overflowY === "auto" || overflowY === "scroll") &&
          parent.scrollHeight > parent.clientHeight;

        if (isScrollable) return parent;

        parent = parent.parentElement;
      }

      return document.scrollingElement || document.documentElement;
    };

    const headerHeight = stickyHeaderRef.current?.offsetHeight || 0;
    const scrollContainer = getScrollContainer(targetElement);

    const targetRect = targetElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const isPageScrollContainer =
      scrollContainer === document.scrollingElement ||
      scrollContainer === document.documentElement;

    const targetPosition = isPageScrollContainer
      ? targetRect.top + window.pageYOffset - headerHeight - 10
      : targetRect.top -
        containerRect.top +
        scrollContainer.scrollTop -
        headerHeight -
        10;

    scrollContainer.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: "smooth",
    });
  };

  const sectionLinks = [
    {
      key: "main-info",
      label: "Основная информация",
      target: "main-info",
    },
    ...(!isNew
      ? [
          {
            key: "access",
            label: "Настройки рабочих групп",
            target: "access",
          },
          {
            key: "groups",
            label: "Группы полей",
            target: "groups",
          },
          {
            key: "title-template",
            label: "Параметры создаваемых задач",
            target: "title-template",
          },
        ]
      : []),
  ];
  return (
    <Flex vertical gap={10}>
      <Flex vertical gap={30}>
        <Flex
          vertical
          gap={10}
          ref={stickyHeaderRef}
          style={{
            position: "sticky",
            top: "-10px",
            background: "#fcfcfc",
            padding: "10px 20px",
            margin: "-10px -20px",
            zIndex: 19,
          }}
        >
          <Breadcrumbs items={breadcrumbItems} />
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              <BackBtn>{data?.name || "Новый тип заявки"}</BackBtn>
              {isDeleted && <Tag color="red">Удален</Tag>}
            </Flex>

            {!isNew && (
              <Button
                type="default"
                onClick={() =>
                  window.open(
                    routes.administration.requestPreview(data.uuid, "default"),
                    "_blank",
                  )
                }
              >
                Предпросмотр
              </Button>
            )}
          </Flex>

          <Space
            className="not-print"
            size={0}
            separator={
              <Divider
                orientation="vertical"
                style={{ borderColor: "var(--color-gray)" }}
              />
            }
          >
            {sectionLinks.map((link) => (
              <a
                key={link.key}
                href={`#${link.target}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(link.target);
                }}
              >
                {link.label}
              </a>
            ))}
          </Space>
        </Flex>
        <Spin spinning={pending}>
          <Flex vertical gap={10}>
            <div id="main-info">
              <MainInfo
                data={data}
                form={form}
                isNew={isNew}
                isDeleted={isDeleted}
                setPending={setPending}
                id={id}
                fetchData={fetchData}
              />
            </div>

            {!isNew && (
              <Card
                id="access"
                title={
                  <Typography.Title level={4} className="no-margin">
                    Настройки рабочих групп
                  </Typography.Title>
                }
              >
                <Flex vertical gap={20}>
                  <AccessCard
                    data={data}
                    isDeleted={isDeleted}
                    fetchData={fetchData}
                  />
                  {data?.access_mode === "unit_restricted" && (
                    <AccessUnitsCard
                      data={data}
                      requestUnits={data?.accessUnits}
                      loading={pending}
                      isDeleted={isDeleted}
                      fetchData={fetchData}
                    />
                  )}
                  <UnitsCard
                    requestUnits={data?.units}
                    loading={pending}
                    isDeleted={isDeleted}
                    isHidden={isHidden}
                    isPublic={isPublic}
                    requestFormUuid={data?.uuid}
                    fetchData={fetchData}
                  />
                </Flex>
              </Card>
            )}

            {!isNew && (
              <div id="groups">
                <GroupsCard
                  data={data}
                  groups={groups}
                  id={id}
                  isDeleted={isDeleted}
                  isDragDisabled={isDragDisabled}
                  addGroup={addGroup}
                  fetchData={fetchData}
                  toggleEdit={toggleEdit}
                  pending={pending}
                  setPending={setPending}
                  setGroups={setGroups}
                />
              </div>
            )}

            {!isNew && (
              <div id="title-template">
                <TaskTitleTemplateCard
                  data={data}
                  groups={groups}
                  isDeleted={isDeleted}
                  loading={pending}
                  setPending={setPending}
                  fetchData={fetchData}
                />
              </div>
            )}

            {isNew && (
              <Typography.Text type="secondary">
                Для добавления групп полей и связи с рабочими группами создайте
                тип заявки
              </Typography.Text>
            )}
          </Flex>
        </Spin>
      </Flex>
    </Flex>
  );
};

export default RequestSettings;
